import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useMyFlags, flagOn } from '../lib/flags.js';
import { vtNavigate } from '../lib/viewTransition.js';
import Icon from './Icon.jsx';

// Home rows for recent posts — two sections sharing ONE card anatomy
// (label chip → media → text → stats → open link) so they read as a family:
//   • SocialPostsRow — the 6 most recent ORGANIC posts across every connected
//     platform (social_post_metrics via /api/my/social-metrics), merged and
//     sorted purely by recency; the chip on each card names the platform.
//   • AppPostsRow — the most recent PUBLISHED posts in the client's Howler-app
//     communities (/api/my/social/posts); the chip is the home community.
// Both hide entirely when there's nothing to show (no connected accounts / no
// posts / flag off — the server routes are already gated, a 403 just leaves the
// row empty). UI hiding rides useMyFlags like the nav.

const PLATFORMS = {
  facebook: { label: 'Facebook', bg: '#1877f2' },
  instagram: { label: 'Instagram', bg: 'linear-gradient(45deg, #f58529, #dd2a7b 55%, #8134af)' },
  tiktok: { label: 'TikTok', bg: '#111', ring: true },
  x: { label: 'X', bg: '#111', ring: true },
  linkedin: { label: 'LinkedIn', bg: '#0a66c2' },
};

export function SocialPostsRow({ entityId, isMobile }) {
  const navigate = useNavigate();
  const flags = useMyFlags(entityId);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!entityId) { setPosts([]); return; }
    let alive = true;
    setPosts([]);
    // sort:'recent' orders by posted_at desc across ALL platforms — no grouping.
    api.mySocial(entityId, { sort: 'recent' })
      .then((r) => { if (alive) setPosts((r.topPosts || []).slice(0, 6)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [entityId]);
  if (!flagOn(flags, 'social') || !posts.length) return null;
  return (
    <>
      <RowHead icon={<Icon name="social" size={15} />} label="Latest on your socials" faint="your most recent posts across every connected platform"
        linkLabel="All social stats →" onLink={() => vtNavigate(navigate, '/social')} />
      <Strip isMobile={isMobile}>
        {posts.map((p) => {
          const plat = PLATFORMS[p.platform] || { label: p.platform, bg: 'var(--muted)' };
          const base = p.reach || p.impressions;
          const eng = base && p.engagement ? (p.engagement / base) * 100 : null;
          return (
            <a key={`${p.platform}|${p.postId}`} className="lift" href={p.permalink || undefined} target="_blank" rel="noreferrer"
              style={{ ...postCard, pointerEvents: p.permalink ? 'auto' : 'none' }}>
              <div style={cardTop}>
                <span style={{ ...chip, background: plat.bg, color: '#fff', boxShadow: plat.ring ? 'inset 0 0 0 1px rgba(255,255,255,0.25)' : 'none' }}>{plat.label}</span>
                <span style={whenStyle}>{timeAgo(p.postedAt)}</span>
              </div>
              <Thumb src={p.mediaThumb} kind={mediaKind(p.mediaType)} video={/video|reel/i.test(p.mediaType || '')} />
              <div style={cardBody}>
                <p style={caption}>{p.caption || '(no caption)'}</p>
                <div style={statsRow}>
                  {p.likes != null && <span>♥ <b style={statB}>{fmtK(p.likes)}</b></span>}
                  {p.comments != null && <span style={statIconWrap}><Icon name="chat" size={12} /> <b style={statB}>{fmtK(p.comments)}</b></span>}
                  {p.shares != null && <span>↗ <b style={statB}>{fmtK(p.shares)}</b></span>}
                  {eng > 0 && <span style={engPill}>{eng.toFixed(1)}% eng</span>}
                </div>
                <div style={reachLine}>
                  {p.reach ? <span>👁 {fmtK(p.reach)} reach</span> : p.impressions ? <span>👁 {fmtK(p.impressions)} impressions</span> : null}
                  {p.videoViews ? <span>▶ {fmtK(p.videoViews)} plays</span> : null}
                </div>
                <div style={{ marginTop: 'auto', paddingTop: 2 }}><span style={openLink}>View post ↗</span></div>
              </div>
            </a>
          );
        })}
      </Strip>
    </>
  );
}

export function AppPostsRow({ entityId, isMobile }) {
  const navigate = useNavigate();
  const flags = useMyFlags(entityId);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!entityId) { setPosts([]); return; }
    let alive = true;
    setPosts([]);
    api.socialPosts('my', entityId)
      .then((r) => { if (alive) setPosts((r.posts || []).filter((p) => p.status === 'published').slice(0, 6)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [entityId]);
  const open = () => vtNavigate(navigate, '/engage/app/posts');
  if (!flagOn(flags, 'community') || !posts.length) return null;
  return (
    <>
      <RowHead icon={<Icon name="app" size={15} />} label="Latest in the app" faint="your posts in the Howler app communities"
        linkLabel="Open Communities →" onLink={open} />
      <Strip isMobile={isMobile}>
        {posts.map((p) => {
          const media = p.media || [];
          const first = media[0];
          return (
            <button key={p.id} className="lift" style={postCard} onClick={open}>
              <div style={cardTop}>
                <span style={communityChip}>{p.community?.name || 'Community'}</span>
                {p.global && <span style={globalBadge}>Global</span>}
                {p.source && p.source !== 'pulse' && <span style={srcBadge}>↪ {p.source[0].toUpperCase() + p.source.slice(1)}</span>}
                <span style={whenStyle}>{timeAgo(p.publishedAt)}</span>
              </div>
              <Thumb src={first?.kind === 'image' ? first.url : ''} video={first?.kind === 'video'}
                kind={first ? (first.kind === 'video' ? '▶ Video' : media.length > 1 ? `${media.length} photos` : 'Photo') : ''} />
              <div style={cardBody}>
                <p style={caption}>{p.body || '(no text)'}</p>
                <div style={statsRow}>
                  <span>♥ <b style={statB}>{fmtK(p.reactionCount || 0)}</b></span>
                  <span style={statIconWrap}><Icon name="chat" size={12} /> <b style={statB}>{fmtK(p.commentCount || 0)}</b></span>
                </div>
                <div style={{ marginTop: 'auto', paddingTop: 2 }}><span style={openLink}>Open post →</span></div>
              </div>
            </button>
          );
        })}
      </Strip>
    </>
  );
}

// ─── shared bits ──────────────────────────────────────────────────────────────

function RowHead({ icon, label, faint, linkLabel, onLink }) {
  return (
    <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', margin: '22px 0 10px', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
      <span>{icon}</span>{label}
      <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--muted)' }}>{faint}</span>
      <button onClick={onLink} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>{linkLabel}</button>
    </h2>
  );
}

// Media block: real thumbnail when we have one (platform CDN / app media), a
// quiet placeholder otherwise; a broken CDN image degrades to the placeholder.
function Thumb({ src, kind, video }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src && !video) return null;
  return (
    <div style={{ position: 'relative', height: 118, flexShrink: 0, background: 'linear-gradient(135deg, rgba(var(--brand-rgb),0.10), rgba(128,128,128,0.10))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {src && !broken
        ? <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : video ? <span style={{ fontSize: 30, color: 'var(--muted)' }}>▶</span> : null}
      {kind && <span style={{ position: 'absolute', left: 9, bottom: 8, fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 980, padding: '2.5px 8px' }}>{kind}</span>}
    </div>
  );
}

// One-row snap carousel — same interaction as the Pinned strip (swipe on
// mobile, chevrons on desktop when it overflows).
function Strip({ isMobile, children }) {
  const ref = useRef(null);
  const [canScroll, setCanScroll] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setCanScroll(el.scrollWidth > el.clientWidth + 8);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);
  const nudge = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth - 80), behavior: 'smooth' });
  return (
    <div style={{ position: 'relative' }}>
      <div ref={ref} style={{ display: 'flex', gap: 12, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 6, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
        {children}
      </div>
      {!isMobile && canScroll && (
        <>
          <button onClick={() => nudge(-1)} aria-label="Scroll left" style={{ ...arrow, left: -12 }}>‹</button>
          <button onClick={() => nudge(1)} aria-label="Scroll right" style={{ ...arrow, right: -12 }}>›</button>
        </>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}
function fmtK(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString('en-ZA');
}
function mediaKind(t) {
  const m = String(t || '').toLowerCase();
  if (/reel/.test(m)) return '▶ Reel';
  if (/video/.test(m)) return '▶ Video';
  if (/carousel|album/.test(m)) return 'Carousel';
  if (/image|photo/.test(m)) return 'Photo';
  return '';
}

const postCard = {
  flex: '0 0 min(86vw, 290px)', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column',
  background: 'var(--tile-bg, var(--card))', border: '1px solid var(--border)', borderRadius: 14,
  boxShadow: 'var(--shadow-sm)', color: 'var(--text)', textAlign: 'left', textDecoration: 'none',
  overflow: 'hidden', cursor: 'pointer', padding: 0, fontFamily: 'inherit',
};
const cardTop = { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 13px 9px', minWidth: 0 };
const chip = { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em', borderRadius: 980, padding: '3px 9px', flexShrink: 0 };
const communityChip = { ...chip, background: 'rgba(var(--brand-rgb), 0.10)', color: 'var(--brand)', display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0, lineHeight: 1.5 };
const globalBadge = { fontSize: 10, fontWeight: 700, borderRadius: 980, padding: '2.5px 8px', background: 'rgba(10,132,255,0.13)', color: '#0a84ff', flexShrink: 0 };
const srcBadge = { fontSize: 10, fontWeight: 700, borderRadius: 980, padding: '2.5px 8px', background: 'rgba(128,128,128,0.13)', color: 'var(--muted-2)', flexShrink: 0 };
const whenStyle = { fontSize: 11, color: 'var(--muted)', marginLeft: 'auto', flexShrink: 0 };
const cardBody = { padding: '10px 13px 12px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 };
const caption = { fontSize: 12.5, lineHeight: 1.5, fontWeight: 600, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const statsRow = { display: 'flex', alignItems: 'center', gap: 11, fontSize: 12, fontWeight: 600, color: 'var(--muted-2)', fontVariantNumeric: 'tabular-nums' };
const statIconWrap = { display: 'inline-flex', alignItems: 'center', gap: 3 };
const statB = { color: 'var(--text)', fontWeight: 700 };
const engPill = { marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, color: 'var(--success)', background: 'rgba(52,199,89,0.14)', borderRadius: 980, padding: '2.5px 8px', flexShrink: 0 };
const reachLine = { display: 'flex', gap: 11, fontSize: 11, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' };
const openLink = { fontSize: 12, fontWeight: 700, color: 'var(--brand)' };
const arrow = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 5, width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 17, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 };
